import React from 'react';
import { Box, Typography, Button, Container, Paper, Avatar, Stack, Link, Divider, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SchoolIcon from '@mui/icons-material/School';
import SecurityIcon from '@mui/icons-material/Security';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import HowToRegIcon from '@mui/icons-material/HowToReg';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import EmailIcon from '@mui/icons-material/Email';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { Link as RouterLink } from 'react-router-dom';
import { useEffect, useState } from 'react';

const testimonials = [
  {
    quote: "A3S Labs' platform made our attendance seamless and our data more reliable. Parents love the real-time updates!",
    name: "Jane Mwangi",
    title: "Principal, Greenfield Academy"
  },
  {
    quote: "The onboarding was fast and the support team is fantastic. Highly recommend to any school!",
    name: "Samuel Otieno",
    title: "Head Teacher, Sunrise School"
  },
  {
    quote: "We save hours every week and our staff is more productive. The analytics are a game changer.",
    name: "Mary Wambui",
    title: "Admin, Starlight Primary"
  }
];

const features = [
  {
    icon: <AnalyticsIcon color="primary" sx={{ fontSize: 40 }} />, 
    title: 'AI Facial Recognition Attendance',
    desc: 'Contactless, accurate check-in for students and staff.'
  },
  {
    icon: <SecurityIcon color="success" sx={{ fontSize: 40 }} />, 
    title: 'Enterprise-Grade Security',
    desc: 'Your data is encrypted and privacy is our top priority.'
  },
  {
    icon: <SchoolIcon color="secondary" sx={{ fontSize: 40 }} />, 
    title: 'All-in-One School Management',
    desc: 'Manage students, teachers, parents, and classes with ease.'
  },
  {
    icon: <EmojiEventsIcon color="warning" sx={{ fontSize: 40 }} />, 
    title: 'Real-Time Analytics',
    desc: 'Instantly view attendance stats, trends, and export reports.'
  },
  {
    icon: <HowToRegIcon color="primary" sx={{ fontSize: 40 }} />, 
    title: 'Mobile-First Experience',
    desc: 'Access everything on any device, anywhere.'
  },
  {
    icon: <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />, 
    title: 'Customizable & Scalable',
    desc: `Tailor the platform to your school's unique needs.`
  }
];

const steps = [
  {
    icon: <HowToRegIcon color="primary" sx={{ fontSize: 36 }} />,
    title: 'Apply',
    desc: `Submit your school's interest form. Our team will review your application.`
  },
  {
    icon: <CheckCircleIcon color="success" sx={{ fontSize: 36 }} />,
    title: 'Get Approved',
    desc: `Once approved, you'll receive a personalized onboarding link.`
  },
  {
    icon: <SchoolIcon color="secondary" sx={{ fontSize: 36 }} />,
    title: 'Onboard',
    desc: 'Complete onboarding and start using the platform!'
  }
];

const valueProps = [
  {
    icon: <SecurityIcon color="primary" sx={{ fontSize: 32 }} />,
    title: 'Proven Results',
    desc: 'Schools using A3S Labs save hours weekly and boost parent engagement.'
  },
  {
    icon: <EmojiEventsIcon color="secondary" sx={{ fontSize: 32 }} />,
    title: 'Dedicated Support',
    desc: 'Our team is with you every step of the way.'
  },
  {
    icon: <AnalyticsIcon color="success" sx={{ fontSize: 32 }} />,
    title: 'Fast Onboarding',
    desc: 'Get started in days, not weeks.'
  },
  {
    icon: <HowToRegIcon color="warning" sx={{ fontSize: 32 }} />,
    title: 'Flexible & Scalable',
    desc: `Grows with your school's needs.`
  }
];

const trustedLogos = [
  '/static/logos/school_1.png',
  '/static/logos/cvision_logo.png',
  '/static/logos/cvision_logo.svg',
];

const faqs = [
  {
    q: "Is my school's data safe?",
    a: "Absolutely. We use industry-leading encryption and privacy practices to keep your data secure."
  },
  {
    q: 'How long does onboarding take?',
    a: 'Most schools are up and running within a few days of approval.'
  },
  {
    q: 'Can I customize the platform?',
    a: "Yes! A3S Labs is built to be flexible and can be tailored to your school's needs."
  },
  {
    q: 'What support do you offer?',
    a: 'Our team provides dedicated onboarding and ongoing support for all clients.'
  }
];

const stats = [
  { label: 'Schools Onboarded', value: 120 },
  { label: 'Hours Saved', value: 5000 },
  { label: 'Active Users', value: 15000 }
];

function AnimatedCounter({ value }: { value: number }) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let start = 0;
    const end = value;
    if (start === end) return;
    let incrementTime = 20;
    let step = Math.ceil(end / 50);
    const timer = setInterval(() => {
      start += step;
      if (start >= end) {
        start = end;
        clearInterval(timer);
      }
      setCount(start);
    }, incrementTime);
    return () => clearInterval(timer);
  }, [value]);
  return <Typography variant="h3" fontWeight={800} color="primary.main">{count.toLocaleString()}</Typography>;
}

const LandingPage: React.FC = () => {
  const [showContact, setShowContact] = useState(false);
  useEffect(() => {
    const onScroll = () => setShowContact(window.scrollY > 300);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <Box sx={{ minHeight: '100vh', background: '#fff', pb: 8 }}>
      {/* Hero Section */}
      <Container maxWidth="md" sx={{ pt: 12, pb: 10 }}>
        <Box textAlign="center">
          <img src="/static/logos/cvision_logo.svg" alt="A3S Labs Logo" style={{ width: 64, height: 64, marginBottom: 16 }} />
          <Typography variant="h2" fontWeight={900} sx={{ mb: 2, color: 'text.primary', letterSpacing: -1, lineHeight: 1.1, fontSize: { xs: 32, sm: 48 } }}>
            AI-Powered School Management & Attendance
          </Typography>
          <Typography variant="h5" sx={{ mb: 4, color: 'text.secondary', fontSize: { xs: 18, sm: 24 } }}>
            Empowering schools with seamless, secure, and smart technology.
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, justifyContent: 'center', gap: 2, mt: 2 }}>
            <Button
              component={RouterLink}
              to="/apply"
              size="large"
              variant="contained"
              color="primary"
              sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 20, borderRadius: 3, boxShadow: 1 }}
              aria-label="Request a Demo or Apply Now"
            >
              Request a Demo / Apply Now
            </Button>
            <Button
              component={RouterLink}
              to="/login"
              size="large"
              variant="outlined"
              color="secondary"
              sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 20, borderRadius: 3, boxShadow: 1 }}
              aria-label="Client Area"
            >
              Client Area
            </Button>
          </Box>
        </Box>
      </Container>

      {/* Stats Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper elevation={0} sx={{ p: 4, borderRadius: 3, background: '#fafbfc', textAlign: 'center', border: '1px solid #f0f0f0' }}>
          <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
            {stats.map((stat, i) => (
              <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: 180, mb: { xs: 2, sm: 0 } }}>
                <AnimatedCounter value={stat.value} />
                <Typography variant="subtitle1" fontWeight={600} color="text.secondary">{stat.label}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4, color: 'text.primary' }}>
          Platform Features
        </Typography>
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
          {features.map((f, i) => (
            <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 45%', md: '1 1 28%' }, minWidth: 220, maxWidth: 340 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3, background: '#fff', textAlign: 'center', border: '1px solid #f0f0f0', height: '100%' }}>
                {f.icon}
                <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{f.title}</Typography>
                <Typography variant="body2" color="text.secondary">{f.desc}</Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* How It Works Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4, color: 'text.primary' }}>
          How It Works
        </Typography>
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
          {steps.map((step, i) => (
            <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: 220, maxWidth: 340 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3, background: '#fafbfc', textAlign: 'center', border: '1px solid #f0f0f0', height: '100%' }}>
                {step.icon}
                <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{step.title}</Typography>
                <Typography variant="body2" color="text.secondary">{step.desc}</Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Value Props Section */}
      <Container maxWidth="lg" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4, color: 'text.primary' }}>
          Why Choose A3S Labs?
        </Typography>
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
          {valueProps.map((v, i) => (
            <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 22%' }, minWidth: 180, maxWidth: 320 }}>
              <Paper elevation={0} sx={{ p: 4, borderRadius: 3, background: '#fff', textAlign: 'center', border: '1px solid #f0f0f0', height: '100%' }}>
                {v.icon}
                <Typography variant="h6" fontWeight={700} sx={{ mt: 2, mb: 1 }}>{v.title}</Typography>
                <Typography variant="body2" color="text.secondary">{v.desc}</Typography>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Testimonials Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4, color: 'text.primary' }}>
          What Our Clients Say
        </Typography>
        <Box display="flex" flexWrap="wrap" justifyContent="center" gap={4}>
          {testimonials.map((t, i) => (
            <Box key={i} sx={{ flex: { xs: '1 1 100%', sm: '1 1 30%' }, minWidth: 220, maxWidth: 340 }}>
              <Paper elevation={1} sx={{ p: 4, borderRadius: 3, background: '#fafbfc', minHeight: 200, border: '1px solid #f0f0f0' }}>
                <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic', color: 'text.primary' }}>
                  {t.quote}
                </Typography>
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Avatar sx={{ bgcolor: 'primary.main', width: 40, height: 40 }}>{t.name[0]}</Avatar>
                  <Box>
                    <Typography variant="subtitle2" fontWeight={700}>{t.name}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.title}</Typography>
                  </Box>
                </Stack>
              </Paper>
            </Box>
          ))}
        </Box>
      </Container>

      {/* Trusted By Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Paper elevation={0} sx={{ p: 3, borderRadius: 3, background: '#fff', textAlign: 'center', border: '1px solid #f0f0f0' }}>
          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, color: 'text.secondary', letterSpacing: 1 }}>
            Trusted by forward-thinking schools
          </Typography>
          <Box display="flex" justifyContent="center" alignItems="center" gap={4} flexWrap="wrap">
            {trustedLogos.map((logo, i) => (
              <img key={i} src={logo} alt="School Logo" style={{ height: 40, width: 'auto', opacity: 0.8 }} />
            ))}
          </Box>
        </Paper>
      </Container>

      {/* FAQ Section */}
      <Container maxWidth="md" sx={{ mb: 8 }}>
        <Typography variant="h4" fontWeight={800} align="center" sx={{ mb: 4, color: 'text.primary' }}>
          Frequently Asked Questions
        </Typography>
        {faqs.map((faq, i) => (
          <Box key={i} sx={{ mb: 2, background: '#fafbfc', borderRadius: 2, border: '1px solid #f0f0f0' }}>
            <Accordion elevation={0} sx={{ background: 'transparent', boxShadow: 'none' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls={`faq-content-${i}`} id={`faq-header-${i}`}>
                <Typography fontWeight={700}>{faq.q}</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>{faq.a}</Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        ))}
      </Container>

      {/* Final CTA */}
      <Container maxWidth="md" sx={{ textAlign: 'center', mt: 8 }}>
        <Button
          component={RouterLink}
          to="/apply"
          size="large"
          variant="contained"
          color="primary"
          sx={{ px: 5, py: 1.5, fontWeight: 700, fontSize: 20, borderRadius: 3, boxShadow: 1 }}
          aria-label="Get Started Now"
        >
          Get Started Now
        </Button>
        <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary', opacity: 0.85 }}>
          No credit card required. Fast onboarding. Friendly support.
        </Typography>
      </Container>

      {/* Floating Contact Us button */}
      <Box sx={{ position: 'fixed', bottom: 32, right: 32, zIndex: 1200, display: showContact ? 'flex' : 'none', flexDirection: 'column', gap: 2 }}>
        <Button
          variant="contained"
          color="secondary"
          sx={{ borderRadius: '50%', minWidth: 56, minHeight: 56, boxShadow: 3 }}
          href="mailto:info@a3slabs.com"
          aria-label="Contact Us"
        >
          <EmailIcon fontSize="large" />
        </Button>
        <Button
          variant="contained"
          color="primary"
          sx={{ borderRadius: '50%', minWidth: 56, minHeight: 56, boxShadow: 3, mt: 1 }}
          onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
          aria-label="Scroll to Top"
        >
          <KeyboardArrowUpIcon fontSize="large" />
        </Button>
      </Box>

      {/* Footer */}
      <Box component="footer" sx={{ mt: 10, py: 5, background: '#fafbfc', color: 'text.secondary', borderTop: '1px solid #f0f0f0' }}>
        <Container maxWidth="lg">
          <Box display="flex" flexWrap="wrap" justifyContent="space-between" alignItems="center" gap={4} sx={{ flexDirection: { xs: 'column', sm: 'row' } }}>
            <Box display="flex" alignItems="center" gap={2} sx={{ flexDirection: { xs: 'column', sm: 'row' }, textAlign: { xs: 'center', sm: 'left' } }}>
              <img src="/static/logos/cvision_logo.svg" alt="A3S Labs Logo" style={{ width: 36, height: 36 }} />
              <Box>
                <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: 2, color: 'text.primary' }}>A3S Labs</Typography>
                <Typography variant="caption" color="text.secondary">Empowering Education with AI</Typography>
              </Box>
            </Box>
            <Box display="flex" gap={4} alignItems="center" sx={{ flexDirection: { xs: 'column', sm: 'row' }, mt: { xs: 2, sm: 0 } }}>
              <Link component={RouterLink} to="/" color="text.secondary" underline="hover" sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 } }} aria-label="Home">Home</Link>
              <Link component={RouterLink} to="/apply" color="text.secondary" underline="hover" sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 } }} aria-label="Apply">Apply</Link>
              <Link href="mailto:info@a3slabs.com" color="text.secondary" underline="hover" sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 } }} aria-label="Contact">Contact</Link>
              <Link href="#" color="text.secondary" underline="hover" sx={{ fontWeight: 600, fontSize: { xs: 16, sm: 18 } }} aria-label="Privacy Policy">Privacy Policy</Link>
            </Box>
            <Box display="flex" gap={2} alignItems="center" sx={{ mt: { xs: 2, sm: 0 } }}>
              <Link href="https://linkedin.com" target="_blank" rel="noopener" color="text.secondary" aria-label="LinkedIn"><LinkedInIcon fontSize="medium" /></Link>
              <Link href="https://twitter.com" target="_blank" rel="noopener" color="text.secondary" aria-label="Twitter"><TwitterIcon fontSize="medium" /></Link>
              <Link href="mailto:info@a3slabs.com" color="text.secondary" aria-label="Email"><EmailIcon fontSize="medium" /></Link>
            </Box>
          </Box>
          <Typography variant="body2" sx={{ mt: 4, textAlign: 'center', color: 'text.disabled' }}>
            © {new Date().getFullYear()} A3S Labs. All rights reserved.
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;